// redux/slices/generalOptionsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { notify, notifyAPIMessages } from "../../../helpers/commonFunctions";
import { authHeaders } from "../../../helpers/axios";
import { apiEndPoints } from "../../../helpers/constants";
import { BASE_URL } from "../../../config";

// Generic thunk to fetch public API options dynamically

export const reqToGetDiscountVariables = createAsyncThunk(
  "reqToGetDiscountVariables",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.discount}?action=${data?.action}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToCreateDiscountProgram = createAsyncThunk(
  "reqToCreateDiscountProgram",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.createProgram}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToCreateCode = createAsyncThunk(
  "reqToCreateCode",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.createCode}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Updated reqToGetProgramList with pagination
export const reqToGetProgramList = createAsyncThunk(
  "reqToGetProgramList",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const { page = 1, limit = 10, ...filters } = data;

      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.programList}?page=${page}&limit=${limit}`,
        {
          ...authHeaders(),
          params: filters,
        }
      );

      onSuccess(response?.data);

      // Return both data and headers
      return {
        data: response.data,
        headers: response.headers,
        totalCounts: response.headers["x-total-count"], // Extract from header
      };
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetCodeList = createAsyncThunk(
  "reqToGetCodeList",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const { page = 1, limit = 10, ...filters } = data;

      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.codeList}?page=${page}&limit=${limit}`,
        {
          ...authHeaders(),
          params: filters,
        }
      );

      onSuccess(response?.data);

      // Return both data and headers
      return {
        data: response.data,
        headers: response.headers,
        totalCount: response.headers["x-total-count"], // Extract from header
      };
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetCodeById = createAsyncThunk(
  "reqToGetCodeById",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.codeList}/${data.id}`,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToUpdateCode = createAsyncThunk(
  "reqToUpdateCode",
  async ({ data = {}, id, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.codeList}/${id}`,
        data, // Send the actual data as request body
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToUpdateCodeStatus = createAsyncThunk(
  "reqToUpdateCodeStatus",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.codeList}/${data.id}`,
        {
          action: data.action, // Dynamic action
        },
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToPauseProgram = createAsyncThunk(
  "reqToPauseProgram",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.codeList}/${data.id}`,
        {
          action: data.action, // "active" or "deactive"
        },
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetApplicationList = createAsyncThunk(
  "reqToGetApplicationList",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const { page, limit, tenant, program, code, createdAt } = data;

      // Build query string dynamically
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page);
      if (limit) queryParams.append("limit", limit);
      if (tenant) queryParams.append("tenant", tenant);
      if (program) queryParams.append("program", program);
      if (code) queryParams.append("code", code);
      if (createdAt) queryParams.append("createdAt", createdAt);

      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.applicationList}?${queryParams.toString()}`,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);

      // Return both data and headers
      return {
        data: response.data,
        headers: response.headers,
        totalCount: response.headers["x-total-count"], // Extract from header
      };
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Add this export function to your existing slice
export const reqToExportReports = createAsyncThunk(
  "reqToExportReports",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.exportDiscount}`,
        data,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetProgramById = createAsyncThunk(
  "reqToGetProgramById",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.programList}/${data.id}`,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Program Status (pause/activate/delete)
export const reqToUpdateProgramStatus = createAsyncThunk(
  "reqToUpdateProgramStatus",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.programList}/${data.id}`,
        {
          action: data.action, // "active", "paused", "deleted"
        },
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Program (edit program details)
export const reqToUpdateProgram = createAsyncThunk(
  "reqToUpdateProgram",
  async ({ data = {}, id, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.programList}/${id}`,
        data,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToExportprogram = createAsyncThunk(
  "reqToExportprogram",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.discountUsageList}`,
        data,
        {
          ...authHeaders(),
        }
      );

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetAnalytics = createAsyncThunk(
  "reqToGetAnalytics",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.analytics}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// export const reqToImportCode = createAsyncThunk(
//   "reqToImportCode",
//   async ({ data, onSuccess, onFailure }, thunkAPI) => {
//     try {
//       const response = await axios.post(
//         `${BASE_URL}${apiEndPoints.importCode}`, // Make sure you have this endpoint in your constants
//         data,
//         authHeaders()
//       );
//       onSuccess(response?.data);
//       return response;
//     } catch (error) {
//       notify(error?.response?.data?.message || "Network Error", "error");
//       onFailure(error);
//       return thunkAPI.rejectWithValue({
//         message: error?.response?.data?.message || "Network Error",
//         status: error?.response?.status,
//       });
//     }
//   }
// );

export const reqToImportCode = createAsyncThunk(
  "reqToImportCode",
  async ({ file, onSuccess, onFailure }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.importCode}`, // Make sure you have this endpoint in your constants
        data,
        authHeaders()
      );

      onSuccess?.(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure?.(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

const DiscountPromotionSlice = createSlice({
  name: "discountCoupons",
  initialState: {
    loader: false,
    error: null,
    discount: [],
    discount_eligibility_rules: [],
    discount_program_types: [],
    createProgramLoader: false,
    createProgramError: null,
    programs: [],
    codelist: [],
    applicationList: [],
    programDetails: null,
    analytics_data: [],
    totalCounts: null,
    applicationTotalCount: null,
    codeTotalCount: null,
  },
  reducers: {
    // Add any specific reducers if needed
    clearProgramDetails: (state) => {
      state.programDetails = null;
    },
  },
  extraReducers: (builder) => {
    // ADD STAFF
    builder.addCase(reqToGetDiscountVariables.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetDiscountVariables.fulfilled, (state, action) => {
      state.loader = false;
      state.discount = action.payload?.data || [];
      (state.discount_eligibility_rules =
        action.payload?.data?.data?.discount_eligibility_rules || []),
        (state.discount_program_types =
          action.payload?.data?.data?.discount_program_types || []);
    });
    builder.addCase(reqToGetDiscountVariables.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // CREATE DISCOUNT PROGRAM
    builder.addCase(reqToCreateDiscountProgram.pending, (state) => {
      state.createProgramLoader = true;
      state.createProgramError = null;
    });
    builder.addCase(reqToCreateDiscountProgram.fulfilled, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = null;
      notify("Program created successfully", "success");
    });
    builder.addCase(reqToCreateDiscountProgram.rejected, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to create program");
    });

    // ADD STAFF
    builder.addCase(reqToGetProgramList.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetProgramList.fulfilled, (state, action) => {
      state.loader = false;
      state.programs = action.payload?.data || [];
      state.totalCounts = parseInt(action.payload.totalCounts) || 0;
    });
    builder.addCase(reqToGetProgramList.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // ADD STAFF
    builder.addCase(reqToGetApplicationList.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetApplicationList.fulfilled, (state, action) => {
      state.loader = false;
      state.applicationList = action.payload?.data?.result || [];
      state.applicationTotalCount = parseInt(action.payload.totalCount) || 0;
    });
    builder.addCase(reqToGetApplicationList.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // ADD STAFF
    builder.addCase(reqToGetCodeList.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetCodeList.fulfilled, (state, action) => {
      state.loader = false;
      state.codelist = action.payload?.data?.result || [];
      state.codeTotalCount = parseInt(action.payload.totalCount) || 0; // Add this line
    });
    builder.addCase(reqToGetCodeList.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // ADD STAFF
    builder.addCase(reqToGetCodeById.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetCodeById.fulfilled, (state, action) => {
      state.loader = false;
      // state.codelist = action.payload?.data?.result || [];
    });
    builder.addCase(reqToGetCodeById.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // CREATE DISCOUNT PROGRAM
    builder.addCase(reqToCreateCode.pending, (state) => {
      state.createProgramLoader = true;
      state.createProgramError = null;
    });
    builder.addCase(reqToCreateCode.fulfilled, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = null;
      notify("Program created successfully", "success");
    });
    builder.addCase(reqToCreateCode.rejected, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to create program");
    });

    builder.addCase(reqToGetProgramById.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetProgramById.fulfilled, (state, action) => {
      state.loader = false;
      state.programDetails = action.payload?.data?.result || null;
    });
    builder.addCase(reqToGetProgramById.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(
        action.payload?.message || "Failed to fetch program details"
      );
    });

    // UPDATE PROGRAM STATUS
    builder.addCase(reqToUpdateProgramStatus.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToUpdateProgramStatus.fulfilled, (state, action) => {
      state.loader = false;
      notify("Program status updated successfully", "success");
    });
    builder.addCase(reqToUpdateProgramStatus.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(
        action.payload?.message || "Failed to update program status"
      );
    });

    // UPDATE PROGRAM
    builder.addCase(reqToUpdateProgram.pending, (state) => {
      state.createProgramLoader = true;
      state.createProgramError = null;
    });
    builder.addCase(reqToUpdateProgram.fulfilled, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = null;
      notify("Program updated successfully", "success");
    });
    builder.addCase(reqToUpdateProgram.rejected, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to update program");
    });

    builder.addCase(reqToGetAnalytics.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetAnalytics.fulfilled, (state, action) => {
      state.loader = false;
      state.analytics_data = action.payload?.data?.result || [];
    });
    builder.addCase(reqToGetAnalytics.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    builder.addCase(reqToImportCode.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToImportCode.fulfilled, (state, action) => {
      state.loader = false;
      notify("Codes imported successfully", "success");
    });
    builder.addCase(reqToImportCode.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to import codes");
    });
  },
});

export const { clearProgramDetails } = DiscountPromotionSlice.actions;

export default DiscountPromotionSlice.reducer;
